import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../App';
import { hasProAccess } from '../../utils/trialHelpers';
import MobileLayout from '../layout/MobileLayout';
import MobileBottomNav from '../layout/MobileBottomNav';
import ProBadge from '../common/ProBadge';
import { toast } from 'sonner';
import { confirmAction } from '../../utils/confirmDialog';
import {
  Archive as PhArchive,
  MagnifyingGlass as PhSearch,
  CheckCircle as PhCheck,
  CalendarBlank as PhCalendar,
  Clock as PhClock,
  Folder as PhFolder,
  MapPin as PhPin,
  Trophy as PhTrophy,
  Confetti as PhConfetti,
  X as PhClose,
  Plus as PhPlus,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ArchivesPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [categories, setCategories] = useState([]);
  
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [allTasksLoaded, setAllTasksLoaded] = useState(false);
  const [currentLimit, setCurrentLimit] = useState(20);
  const [hasMoreTasks, setHasMoreTasks] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    priority: '',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    // Check if user has Pro access
    if (!hasProAccess(user)) {
      // Show Pro-only message
      return;
    }
    fetchArchivedTasks(20); // Initially load only 20 tasks
    fetchCategories();
  }, [user]);

  const fetchArchivedTasks = async (limit) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/tasks/archived?limit=${limit}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setArchivedTasks(response.data);
      
      // Check if there might be more tasks
      // If we got exactly the limit, there might be more
      setHasMoreTasks(response.data.length >= limit);
      
      // Update current limit
      setCurrentLimit(limit);
    } catch (error) {
      console.error('Error fetching archived tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreTasks = () => {
    const newLimit = currentLimit + 20;
    fetchArchivedTasks(newLimit);
  };

  const loadAllTasks = () => {
    fetchArchivedTasks(1000);
    setAllTasksLoaded(true);
    setShowFilters(true); // Also open filters when loading all
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/api/categories`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleBulkRestore = async () => {
    if (selectedTasks.length === 0) {
      toast.info(t('pleaseSelectTasks') || 'Please select tasks first');
      return;
    }

    const n = selectedTasks.length;
    const label = `${n} task${n === 1 ? '' : 's'}`;
    if (await confirmAction({ title: `Restore ${label}?`, message: 'They will move back to your active tasks.', confirmLabel: t('restore') })) {
      try {
        await axios.post(`${API}/api/tasks/bulk-restore`, {
          task_ids: selectedTasks
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        // Remove restored tasks from UI
        setArchivedTasks(prev => prev.filter(t => !selectedTasks.includes(t.id)));
        setSelectedTasks([]);
        setIsSelectionMode(false);
        toast.success(`${label} restored`);
      } catch (error) {
        console.error('Error restoring tasks:', error);
        toast.error('Failed to restore tasks');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTasks.length === 0) {
      toast.info(t('pleaseSelectTasks') || 'Please select tasks first');
      return;
    }

    const n = selectedTasks.length;
    const label = `${n} task${n === 1 ? '' : 's'}`;
    if (await confirmAction({ title: `Delete ${label} permanently?`, message: 'This cannot be undone.', confirmLabel: t('delete'), destructive: true })) {
      try {
        await axios.post(`${API}/api/tasks/bulk-delete`, {
          task_ids: selectedTasks
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        // Remove deleted tasks from UI
        setArchivedTasks(prev => prev.filter(t => !selectedTasks.includes(t.id)));
        setSelectedTasks([]);
        setIsSelectionMode(false);
        toast.success(`${label} deleted`);
      } catch (error) {
        console.error('Error deleting tasks:', error);
        toast.error('Failed to delete tasks');
      }
    }
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const filteredTasks = useMemo(() => {
    let filtered = [...archivedTasks];
    
    // Search by title
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(task => 
        task.title?.toLowerCase().includes(searchLower)
      );
    }
    
    // Filter by category
    if (filters.category) {
      filtered = filtered.filter(task => task.category === filters.category);
    }
    
    // Filter by priority
    if (filters.priority) {
      filtered = filtered.filter(task => task.priority === filters.priority);
    }
    
    // Filter by date range
    if (filters.dateFrom) {
      filtered = filtered.filter(task => 
        task.due_date && new Date(task.due_date) >= new Date(filters.dateFrom)
      );
    }
    if (filters.dateTo) {
      filtered = filtered.filter(task => 
        task.due_date && new Date(task.due_date) <= new Date(filters.dateTo)
      );
    }
    
    return filtered;
  }, [archivedTasks, filters, searchQuery]);

  // "Wins" stats — celebrate completed work rather than treating archives
  // as a graveyard. We compute three numbers from the loaded archives:
  //   - all-time count (cap shown is "100+" when only the first page is
  //     loaded so we don't lie about the total)
  //   - tasks archived this calendar month
  //   - longest weekly streak of completed tasks (rolling 7-day windows)
  const winsStats = useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const total = archivedTasks.length;
    const thisMonth = archivedTasks.filter((t) => {
      const d = t.archived_at ? new Date(t.archived_at) : (t.completed_at ? new Date(t.completed_at) : null);
      return d && d >= startOfMonth;
    }).length;
    // Best 7-day rolling window
    const dates = archivedTasks
      .map((t) => t.archived_at || t.completed_at)
      .filter(Boolean)
      .map((d) => new Date(d).getTime())
      .sort((a, b) => a - b);
    let best = 0;
    if (dates.length) {
      const WINDOW = 7 * 24 * 60 * 60 * 1000;
      let i = 0;
      for (let j = 0; j < dates.length; j++) {
        while (dates[j] - dates[i] > WINDOW) i++;
        best = Math.max(best, j - i + 1);
      }
    }
    return { total, thisMonth, bestWeek: best };
  }, [archivedTasks]);

  // Group filtered tasks by Month-Year for a "scrapbook" feel rather than
  // an undifferentiated dump. Most-recent month first.
  const groupedTasks = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });
    const groups = new Map();
    for (const t of filteredTasks) {
      const raw = t.archived_at || t.completed_at || t.updated_at || t.created_at;
      const d = raw ? new Date(raw) : null;
      const key = d ? `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}` : 'unknown';
      const label = d ? fmt.format(d) : 'Earlier';
      if (!groups.has(key)) groups.set(key, { key, label, when: d ? d.getTime() : 0, items: [] });
      groups.get(key).items.push(t);
    }
    return Array.from(groups.values()).sort((a, b) => b.when - a.when);
  }, [filteredTasks]);

  return (
    <MobileLayout
      title={t('archives')}
      showBack={true}
      onBack={() => navigate('/', { replace: true })}
    >
      {/* Pro-Only Access Check */}
      {!hasProAccess(user) ? (
        <div className="flex items-center justify-center px-4 py-12">
          <div className="max-w-md w-full text-center bg-white rounded-2xl border border-gray-200 p-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 mx-auto mb-5 flex items-center justify-center">
              <PhTrophy size={32} weight="duotone" className="text-blue-600" />
            </div>
            <div className="mb-4 inline-block">
              <ProBadge size="lg" className="text-sm px-4 py-1.5" />
            </div>
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-2 tracking-tight">
              Your Hall of Fame
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Upgrade to Pro to keep a record of every task you've crossed off — celebrate your wins, search past work, and keep your dashboard clean.
            </p>
            <button
              onClick={() => navigate('/payment')}
              data-testid="archives-upgrade-cta"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all"
            >
              Try 14 days free
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* "Wins" hero — celebrates past work with three big numbers. */}
          <div
            className="rounded-2xl border border-gray-200 bg-white p-5 mb-4"
            data-testid="archives-wins-hero"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                <PhTrophy size={20} weight="duotone" className="text-blue-600" />
              </div>
              <div className="min-w-0">
                <h2 className="font-display text-lg font-bold text-gray-900 tracking-tight">
                  Your wins
                </h2>
                <p className="text-xs text-gray-500">Every task here is a battle you won.</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Archived" value={winsStats.total} suffix={!allTasksLoaded && winsStats.total >= 20 ? '+' : ''} />
              <Stat label="This month" value={winsStats.thisMonth} accent="emerald" />
              <Stat label="Best week" value={winsStats.bestWeek} accent="indigo" hint="rolling 7d" />
            </div>
          </div>

          {/* Load All Archives Info - Show when only 20 tasks are loaded */}
      {!allTasksLoaded && archivedTasks.length >= 20 && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 shadow-sm mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-800 font-medium">{t('showingFirstTwentyTasks') || 'Showing first 20 archived tasks'}</p>
              <p className="text-xs text-blue-600">{t('loadAllArchives') || 'Load all archives to access search and filters'}</p>
            </div>
            <button
              onClick={loadAllTasks}
              disabled={loading}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                loading 
                  ? 'bg-gray-400 text-white cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {loading && (
                <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              <span>{loading ? `${t('loading')}...` : t('allArchives')}</span>
            </button>
          </div>
        </div>
      )}

      {/* All Archives Button - Toggle Filters (only show when all tasks loaded) */}
      {allTasksLoaded && (
        <div className="mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full py-3 px-4 rounded-lg font-medium transition-all bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg hover:shadow-xl flex items-center justify-between"
          >
            <span>{t('allArchives')}</span>
            <svg 
              className={`w-5 h-5 transition-transform ${showFilters ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      )}

      {/* Search Bar - Only show when all tasks loaded */}
      {allTasksLoaded && showFilters && (
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder={t('searchByTitle') || 'Search by title...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Filter Controls - Only show when all tasks loaded */}
      {allTasksLoaded && showFilters && (
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
          <h3 className="font-semibold text-gray-900 mb-3">{t('filters')}</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <select
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">{t('allCategories')}</option>
                {categories.map(category => (
                  <option key={category.id || category.name} value={category.name}>{category.name}</option>
                ))}
              </select>
              <select
                value={filters.priority}
                onChange={(e) => setFilters({...filters, priority: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">{t('allPriorities') || 'All Priorities'}</option>
                <option value="High">{t('high')}</option>
                <option value="Medium">{t('medium')}</option>
                <option value="Low">{t('low')}</option>
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">{t('fromDate') || 'From Date'}</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">{t('toDate') || 'To Date'}</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            <button
              onClick={() => {
                setFilters({ category: '', priority: '', dateFrom: '', dateTo: '' });
                setSearchQuery('');
              }}
              className="w-full py-2 px-4 bg-gray-200 text-gray-800 rounded-lg font-medium text-sm hover:bg-gray-300 transition-colors"
            >
              {t('clearAllFilters') || 'Clear All Filters'}
            </button>
          </div>
        </div>
      )}

      {/* Selection Mode Actions */}
      {isSelectionMode && (
        <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">
              {selectedTasks.length} {t('selected')}
            </span>
            <button
              onClick={() => {
                setIsSelectionMode(false);
                setSelectedTasks([]);
              }}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              {t('cancel')}
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleBulkRestore}
              className="flex-1 py-2 px-4 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600"
            >
              {t('restore')}
            </button>
            <button
              onClick={handleBulkDelete}
              className="flex-1 py-2 px-4 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600"
            >
              {t('delete')}
            </button>
          </div>
        </div>
      )}

      {/* Select Tasks Button */}
      {!isSelectionMode && archivedTasks.length > 0 && (
        <button
          onClick={() => setIsSelectionMode(true)}
          className="w-full mb-4 py-2 px-4 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
        >
          {t('selectTasks') || 'Select Tasks'}
        </button>
      )}

      {/* Archived Tasks List — grouped by Month/Year. */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">{t('loadingTasks')}</div>
      ) : filteredTasks.length === 0 ? (
        <div
          className="rounded-2xl border border-gray-200 bg-white px-6 py-12 text-center"
          data-testid="archives-empty-state"
        >
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 mx-auto mb-4 flex items-center justify-center">
            <PhConfetti size={32} weight="duotone" className="text-blue-600" />
          </div>
          <h3 className="font-display text-lg font-semibold text-gray-900 mb-1 tracking-tight">
            Nothing here yet
          </h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto mb-4">
            {searchQuery || filters.category || filters.priority
              ? 'No archived tasks match those filters.'
              : 'Your completed wins will live here. Finish a task and archive it to start your collection.'}
          </p>
          <button
            onClick={() => navigate('/')}
            data-testid="archives-empty-cta"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-800 transition-colors"
          >
            <PhPlus size={16} weight="bold" />
            Back to active tasks
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {groupedTasks.map((group) => (
            <section key={group.key} data-testid={`archives-group-${group.key}`}>
              <header className="flex items-baseline justify-between mb-2 px-1">
                <h3 className="font-display text-xs font-semibold text-gray-500 uppercase tracking-[0.2em]">
                  {group.label}
                </h3>
                <span className="font-mono text-xs text-gray-400">
                  {group.items.length} task{group.items.length === 1 ? '' : 's'}
                </span>
              </header>
              <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden divide-y divide-gray-100">
                {group.items.map((task) => (
                  <ArchivedRow
                    key={task.id}
                    task={task}
                    isSelectionMode={isSelectionMode}
                    isSelected={selectedTasks.includes(task.id)}
                    onSelect={() => toggleTaskSelection(task.id)}
                    onOpen={() => navigate(`/tasks/${task.id}`)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Load More Button - Show when not all tasks loaded and there might be more */}
      {!allTasksLoaded && hasMoreTasks && archivedTasks.length > 0 && (
        <div className="mt-6 mb-4">
          <button
            onClick={loadMoreTasks}
            disabled={loading}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center space-x-2 ${
              loading 
                ? 'bg-gray-400 text-white cursor-not-allowed' 
                : 'bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50'
            }`}
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>{t('loading')}...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                <span>{t('loadMoreTasks') || 'Load More Tasks'} (20 {t('more') || 'more'})</span>
              </>
            )}
          </button>
        </div>
      )}

      <MobileBottomNav />
      </>
      )}
    </MobileLayout>
  );
};

export default ArchivesPage;

// ---------------------------------------------------------------------
// Local presentational helpers
// ---------------------------------------------------------------------

const Stat = ({ label, value, suffix = '', accent = 'blue', hint }) => {
  const colorMap = {
    blue:    'text-blue-700',
    indigo:  'text-indigo-700',
    emerald: 'text-emerald-700',
  };
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3" data-testid={`archives-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-gray-500 mb-1">{label}</p>
      <p className={`font-display text-2xl font-bold tracking-tight ${colorMap[accent] || colorMap.blue}`}>
        {value}{suffix}
      </p>
      {hint && <p className="font-mono text-[10px] text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
};

const ArchivedRow = ({ task, isSelectionMode, isSelected, onSelect, onOpen }) => {
  const priorityClass =
    task.priority === 'High'   ? 'bg-rose-50 text-rose-700' :
    task.priority === 'Medium' ? 'bg-amber-50 text-amber-700' :
                                 'bg-emerald-50 text-emerald-700';
  return (
    <div
      onClick={() => (isSelectionMode ? onSelect() : onOpen())}
      className={`group flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50 ${
        isSelectionMode && isSelected ? 'bg-blue-50/60' : ''
      }`}
      data-testid={`archived-task-row-${task.id}`}
    >
      {isSelectionMode ? (
        <button
          onClick={(e) => { e.stopPropagation(); onSelect(); }}
          className={`w-5 h-5 rounded-md border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
            isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300 group-hover:border-blue-400'
          }`}
          aria-label="Toggle selection"
        >
          {isSelected && (
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
      ) : (
        <div className="w-7 h-7 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
          <PhCheck size={16} weight="fill" className="text-emerald-600" />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-800 truncate" title={task.title}>{task.title}</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-xs text-gray-500">
          {task.due_date && (
            <span className="inline-flex items-center gap-1 font-mono">
              <PhCalendar size={12} />
              {new Date(task.due_date).toLocaleDateString()}
              {task.time && <><PhClock size={12} className="ml-1" /> {task.time}</>}
            </span>
          )}
          {task.category && (
            <span className="inline-flex items-center gap-1">
              <PhFolder size={12} />
              {task.category}
            </span>
          )}
          {task.location && (
            <span className="inline-flex items-center gap-1 truncate" title={task.location}>
              <PhPin size={12} />
              {task.location}
            </span>
          )}
        </div>
      </div>

      <span className={`hidden sm:inline-block flex-shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium ${priorityClass}`}>
        {task.priority}
      </span>
    </div>
  );
};
